import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Container,
  Typography,
  Divider,
  Stack,
  Menu,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import PolicyRoundedIcon from '@mui/icons-material/PolicyRounded';
import ContactSupportRoundedIcon from '@mui/icons-material/ContactSupportRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import TranslateIcon from '@mui/icons-material/Translate';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { homeTranslations } from '../../locales/homeTranslations';
import './Header.css';

const logo = '/arc-logo2.png';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const tNav = homeTranslations[currentLanguage]?.nav || homeTranslations.en.nav;

  const navItems = [
    { key: 'home', name: tNav.home, path: '/', section: 'home', icon: <HomeRoundedIcon fontSize="small" /> },
    { key: 'about', name: tNav.about, path: '/', section: 'about', icon: <InfoRoundedIcon fontSize="small" /> },
    { key: 'journals', name: tNav.journals, path: '/journals', section: 'journal', icon: <MenuBookRoundedIcon fontSize="small" /> },
    { key: 'procedure', name: tNav.procedure, path: '/', section: 'publication-procedure', icon: <AssignmentTurnedInRoundedIcon fontSize="small" /> },
    { key: 'benefits', name: tNav.benefits, path: '/', section: 'benefits-of-publishing-paper-in-ARC-Publication-Services-LLC', icon: <StarRoundedIcon fontSize="small" /> },
    { key: 'guidelines', name: tNav.guidelines, path: '/guidelines', icon: <DescriptionRoundedIcon fontSize="small" /> },
    { key: 'policies', name: tNav.policies, path: '/policies', icon: <PolicyRoundedIcon fontSize="small" /> },
    { key: 'contact', name: tNav.contact, path: '/contact', icon: <ContactSupportRoundedIcon fontSize="small" />, hideOnDesktopNav: true },
  ];

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleSelectLanguage = (code) => {
    changeLanguage(code);
    handleLangMenuClose();
  };

  // Handle scroll detection for sticky navbar effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }

      // If on home page, determine active section based on scroll position
      if (location.pathname === '/') {
        const sections = ['home', 'about', 'areas-we-cover', 'journal', 'publication-procedure', 'benefits-of-publishing-paper-in-ARC-Publication-Services-LLC'];
        const scrollPosition = window.scrollY + 140;

        for (let i = sections.length - 1; i >= 0; i--) {
          const el = document.getElementById(sections[i]);
          if (el && el.offsetTop <= scrollPosition) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled, location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const scrollToSection = useCallback((sectionId) => {
    if (typeof window === 'undefined') return;

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 110;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setActiveSection(sectionId);
      if (window.history.pushState) {
        window.history.pushState(null, null, `#${sectionId}`);
      } else {
        window.location.hash = `#${sectionId}`;
      }
    }
  }, []);

  // Check URL hash on page load or location change to scroll
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 150);
    }
  }, [location, scrollToSection]);

  const handleNavItemClick = (item) => (event) => {
    setMobileOpen(false);

    if (item.section) {
      event.preventDefault();
      if (location.pathname !== '/') {
        navigate(`/#${item.section}`);
      } else {
        scrollToSection(item.section);
      }
    } else {
      if (location.pathname === item.path) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 80);
      }
    }
  };

  // Determine if a nav item is currently active
  const isItemActive = (item) => {
    if (location.pathname === '/') {
      if (item.section) {
        return activeSection === item.section;
      }
      return location.pathname === item.path && !item.section;
    }
    return location.pathname === item.path;
  };

  // Mobile Drawer Layout
  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>
      {/* Drawer Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          background: 'linear-gradient(to right, #f8fafc, #ffffff)'
        }}
      >
        <Box
          component={Link}
          to="/"
          onClick={() => setMobileOpen(false)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none' }}
        >
          <Box
            component="img"
            src={logo}
            alt="ARC Publication Logo"
            sx={{ height: 42, width: 'auto' }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              ARC Publication
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
              Academic & Research
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          aria-label="Close navigation menu"
          sx={{
            color: '#64748b',
            bgcolor: 'rgba(0,0,0,0.04)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)', color: '#0f172a' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Drawer Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {/* Call-to-Action Card inside Drawer */}
        <Box className="arc-drawer-cta-card">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#05DBF2', display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <SupportAgentRoundedIcon sx={{ fontSize: 18 }} />
            Editorial Support & Inquiry
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.8rem', mb: 2 }}>
            Have questions about manuscript submission, peer review, or publishing procedures? Get in touch with our editorial team.
          </Typography>
          <Button
            component={Link}
            to="/contact"
            onClick={() => setMobileOpen(false)}
            variant="contained"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
            sx={{
              background: 'linear-gradient(135deg, #4D49BF 0%, #027b9a 50%, #05DBF2 100%)',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '0.85rem',
              py: 1,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(77, 73, 191, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3f3ba6 0%, #026b86 50%, #02b6ca 100%)',
              }
            }}
          >
            {tNav.editorialOffice}
          </Button>
        </Box>

        {/* Mobile Language Selector Chips */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" sx={{ px: 1, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
            {tNav.selectLanguage}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 0.5 }}>
            {languages.map((lang) => (
              <Button
                key={lang.code}
                size="small"
                onClick={() => handleSelectLanguage(lang.code)}
                variant={lang.code === currentLanguage ? 'contained' : 'outlined'}
                sx={{
                  py: 0.4,
                  px: 1.2,
                  borderRadius: '16px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto',
                  bgcolor: lang.code === currentLanguage ? '#4D49BF' : 'transparent',
                  color: lang.code === currentLanguage ? '#ffffff' : '#475569',
                  borderColor: lang.code === currentLanguage ? '#4D49BF' : '#cbd5e1',
                  '&:hover': {
                    bgcolor: lang.code === currentLanguage ? '#3f3ba6' : 'rgba(77, 73, 191, 0.08)',
                  }
                }}
              >
                {lang.nativeName}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Navigation List */}
        <Typography variant="caption" sx={{ px: 1, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {tNav.navigation}
        </Typography>
        <List sx={{ mt: 0.5, p: 0 }}>
          {navItems.map((item) => {
            const active = isItemActive(item);
            return (
              <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={handleNavItemClick(item)}
                  selected={active}
                  sx={{
                    borderRadius: '10px',
                    py: 1.1,
                    px: 1.5,
                    color: active ? '#4D49BF' : '#334155',
                    bgcolor: active ? 'rgba(77, 73, 191, 0.08)' : 'transparent',
                    borderLeft: active ? '4px solid #4D49BF' : '4px solid transparent',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(77, 73, 191, 0.08)',
                      '&:hover': {
                        bgcolor: 'rgba(77, 73, 191, 0.12)',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.03)',
                      color: '#4D49BF',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: active ? '#4D49BF' : '#64748b' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: '0.92rem',
                      fontWeight: active ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 2.5, borderColor: 'rgba(0, 0, 0, 0.06)' }} />

        {/* Quick Contact Info */}
        <Typography variant="caption" sx={{ px: 1, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Direct Editorial Contact
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5, px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <EmailOutlinedIcon sx={{ fontSize: 18, color: '#027b9a' }} />
            <a href="mailto:contact@arcpublishing.org" style={{ textDecoration: 'none', color: '#475569', fontSize: '0.85rem' }}>
              contact@arcpublishing.org
            </a>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <PhoneInTalkOutlinedIcon sx={{ fontSize: 18, color: '#027b9a' }} />
            <a href="tel:+971508370576" style={{ textDecoration: 'none', color: '#475569', fontSize: '0.85rem' }}>
              +971 50 837 0576
            </a>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#027b9a', mt: 0.2 }} />
            <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.8rem', lineHeight: 1.4 }}>
              Office 118, Al Qusais 2nd, Dubai, UAE
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Top Progress / Shimmer Line */}
      <Box className="arc-top-shimmer" />

      {/* Top Academic Trust / Contact Bar */}
      <Box
        className="arc-top-bar"
        sx={{
          display: { xs: 'none', lg: 'block' },
          transform: scrolled ? 'translateY(-100%)' : 'translateY(0)',
          height: scrolled ? 0 : 'auto',
          opacity: scrolled ? 0 : 1,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Contact details */}
            <Stack direction="row" spacing={3} alignItems="center">
              <Box component="a" href="mailto:contact@arcpublishing.org">
                <EmailOutlinedIcon sx={{ fontSize: 15, color: '#05DBF2' }} />
                <span>contact@arcpublishing.org</span>
              </Box>
              <Box component="a" href="tel:+971508370576">
                <PhoneInTalkOutlinedIcon sx={{ fontSize: 15, color: '#05DBF2' }} />
                <span>+971 50 837 0576</span>
              </Box>

            </Stack>

            {/* Trust Badges & Language Selector */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box className="arc-badge-pill">
                <span className="arc-badge-dot" />
                <span>{tNav.fastTrack}</span>
              </Box>

              {/* Language Switcher Dropdown */}
              <Button
                size="small"
                onClick={handleLangMenuOpen}
                startIcon={<TranslateIcon sx={{ fontSize: 16, color: '#05DBF2' }} />}
                endIcon={<ArrowDropDownRoundedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />}
                sx={{
                  color: '#e2e8f0',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  py: 0.3,
                  px: 1.2,
                  borderRadius: '16px',
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.16)',
                    borderColor: '#05DBF2'
                  }
                }}
              >
                {languages.find(l => l.code === currentLanguage)?.nativeName || 'English'}
              </Button>

              <Menu
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
                elevation={4}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 150,
                    borderRadius: 2,
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    border: '1px solid #e2e8f0'
                  }
                }}
              >
                {languages.map((lang) => (
                  <MenuItem
                    key={lang.code}
                    selected={lang.code === currentLanguage}
                    onClick={() => handleSelectLanguage(lang.code)}
                    sx={{
                      fontSize: '0.88rem',
                      fontWeight: lang.code === currentLanguage ? 700 : 500,
                      color: lang.code === currentLanguage ? '#4D49BF' : '#334155',
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1,
                      px: 2
                    }}
                  >
                    <span>{lang.nativeName}</span>
                    <Typography variant="caption" sx={{ color: '#94a3b8', ml: 1.5, fontSize: '0.75rem' }}>
                      {lang.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Main Navigation Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: { xs: 0, lg: scrolled ? 0 : '34px' },
          backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.96)' : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          color: '#0f172a',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: scrolled
            ? '0 10px 30px -10px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.05)'
            : '0 2px 12px rgba(0, 0, 0, 0.04)',
          borderBottom: '1px solid',
          borderColor: scrolled ? 'rgba(226, 232, 240, 0.9)' : 'rgba(226, 232, 240, 0.6)',
          zIndex: 1200,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              height: { xs: 68, md: scrolled ? 70 : 80 },
              minHeight: { xs: 68, md: scrolled ? 70 : 80 },
              justifyContent: 'space-between',
              transition: 'all 0.3s ease'
            }}
          >
            {/* Logo and Brand Identity */}
            <Box
              component={Link}
              to="/"
              onClick={() => {
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                gap: { xs: 1, sm: 1.5 },
                mr: 2
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="ARC Publication Services LLC"
                sx={{
                  height: { xs: 38, sm: 44, md: scrolled ? 44 : 50 },
                  width: 'auto',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.04)',
                  },
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.25rem' },
                    lineHeight: 1.15,
                    color: '#0f172a',
                    letterSpacing: '-0.02em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <Box component="span" sx={{ color: '#4D49BF' }}>ARC</Box>
                  <Box component="span">Publication</Box>
                </Typography>
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontSize: { xs: '0.62rem', sm: '0.7rem' },
                    fontWeight: 600,
                    color: '#64748b',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase'
                  }}
                >
                  Services LLC
                </Typography>
              </Box>
            </Box>

            {/* Desktop Navigation Links - Fully Responsive */}
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                gap: { lg: 0.3, xl: 0.8 },
                flexWrap: 'nowrap',
              }}
            >
              {navItems
                .filter((item) => !item.hideOnDesktopNav)
                .map((item) => {
                  const active = isItemActive(item);
                  return (
                    <Button
                      key={item.key}
                      component={Link}
                      to={item.path}
                      onClick={handleNavItemClick(item)}
                      className={`arc-nav-btn ${active ? 'active' : ''}`}
                      sx={{
                        fontSize: { lg: '0.82rem', xl: '0.88rem' },
                        px: { lg: 0.9, xl: 1.3 },
                        py: { lg: 0.5, xl: 0.7 },
                        whiteSpace: 'nowrap',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {item.name}
                    </Button>
                  );
                })}
            </Box>

            {/* Desktop Action (Contact Us CTA Button) */}
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', ml: { lg: 0.5, xl: 1 } }}>
              <Button
                component={Link}
                to="/contact"
                className="arc-cta-btn"
                startIcon={<SupportAgentRoundedIcon sx={{ fontSize: '1.1rem' }} />}
                onClick={() => {
                  if (location.pathname === '/contact') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                sx={{
                  fontSize: { lg: '0.84rem', xl: '0.88rem' },
                  px: { lg: 1.8, xl: 2.2 },
                  py: { lg: 0.7, xl: 0.9 },
                  whiteSpace: 'nowrap',
                }}
              >
                {tNav.contactBtn}
              </Button>
            </Box>

            {/* Mobile / Tablet Menu Actions (Visible on screens < lg) */}
            <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', gap: 0.8 }}>
              {/* Mobile Language Button */}
              <IconButton
                size="small"
                onClick={handleLangMenuOpen}
                aria-label="Change Language"
                sx={{
                  color: '#4D49BF',
                  bgcolor: 'rgba(77, 73, 191, 0.08)',
                  borderRadius: '10px',
                  p: 0.8,
                  '&:hover': { bgcolor: 'rgba(77, 73, 191, 0.15)' }
                }}
              >
                <TranslateIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <Button
                component={Link}
                to="/contact"
                variant="contained"
                size="small"
                startIcon={<SupportAgentRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  background: 'linear-gradient(135deg, #4D49BF 0%, #05DBF2 100%)',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '20px',
                  px: 1.5,
                  py: 0.6,
                  boxShadow: '0 2px 8px rgba(77, 73, 191, 0.35)'
                }}
              >
                {tNav.contactMobile}
              </Button>
              <IconButton
                color="inherit"
                aria-label="Open Navigation Menu"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  color: '#0f172a',
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '10px',
                  p: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Box component="nav">
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
            disableScrollLock: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { xs: '85%', sm: '360px' },
              maxWidth: '100%',
              height: '100%',
              border: 'none',
              boxShadow: '-8px 0 35px rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
};

export default Header;